// VOY-485 (rebuild) — Traveler DNA / Quiz (Public profile tab).
//
// DECLARED INPUT (via quiz launcher). Mirrors the live profile's
// `TravelerDnaCard`: primary label + secondary traits + 8-dimension
// spectrum visualization from `quiz_results.traveler_vector`, plus a
// "Retake quiz" CTA that opens the live `QuizModal`.
//
// The agent's archetype (VOY-503 output, rendered on the You page) is
// DERIVED from this vector + bookings — it is not this. Quiz vector is
// the user's declared self-assessment; archetype is the agent's
// inferred summary. Different things, different surfaces.
//
// Empty state: invites the user to take the quiz. New users have no
// vector — the section still renders at stable dimensions so the page
// doesn't reflow when the data lands.

import { Sparkles } from 'lucide-react'
import {
  getDimensionSpectrum,
  getPersonalityTags,
  getTravelerVector,
  type QuizResultForProfile,
} from '@/components/profile/helpers/quiz-helpers'

export interface TravelerDnaSectionProps {
  quiz: QuizResultForProfile | null
  onTakeQuiz: () => void
}

export function TravelerDnaSection({ quiz, onTakeQuiz }: TravelerDnaSectionProps) {
  const travelerProfile = getTravelerVector(quiz)
  const spectrum = travelerProfile ? getDimensionSpectrum(travelerProfile, 4) : []
  const tags = travelerProfile
    ? [travelerProfile.primary_label, ...(travelerProfile.secondary_traits ?? [])].filter(
        (v): v is string => !!v,
      )
    : getPersonalityTags(quiz)
  const hasData = tags.length > 0 || spectrum.length > 0

  return (
    <section className="card" aria-labelledby="dna-h">
      <div className="card-h">
        <h3 id="dna-h">Traveler DNA</h3>
        {hasData && (
          <button type="button" className="btn-ghost" onClick={onTakeQuiz} style={{ padding: '5px 12px', fontSize: 11.5 }}>
            Retake quiz
          </button>
        )}
      </div>

      {hasData ? (
        <>
          {tags.length > 0 && (
            <div className="row" style={{ gap: 8, marginBottom: 14 }}>
              {tags.map((tag) => (
                <span key={tag} className="pill purple">{tag}</span>
              ))}
            </div>
          )}

          {spectrum.length > 0 && (
            <div className="stack sm" style={{ gap: 12 }}>
              {spectrum.map(({ dim, position, leftLabel, rightLabel }) => (
                <div key={dim}>
                  <div className="row between" style={{ marginBottom: 4, fontSize: 11, color: 'var(--v6-fg-muted)' }}>
                    <span>{leftLabel}</span>
                    <span>{rightLabel}</span>
                  </div>
                  <div className="dna-track">
                    <div className="dna-marker" style={{ left: `${position * 100}%` }} aria-hidden="true" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="stack sm" style={{ alignItems: 'flex-start' }}>
          <p style={{ color: 'var(--v6-fg-muted)', fontSize: 13, margin: '0 0 8px' }}>
            Take the quiz to unlock your travel personality — feeds the agent so it can match you to the right trips.
          </p>
          <button type="button" className="btn-primary" onClick={onTakeQuiz} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles aria-hidden="true" style={{ width: 14, height: 14 }} />
            Take the quiz
          </button>
        </div>
      )}
    </section>
  )
}
